/* ============================================================
   DHAAGA TRIBE — Desktop nav dropdown controller (v3)
   Replaces the desktop (mouse) behaviour of the header dropdowns
   in app.js, which opened panels from several independent
   triggers (mouseenter with a lingering close, click toggles, CSS
   :hover). Wide invisible panels and hover bridges from *other*
   menus sat in the path of the pointer, so moving down from one
   trigger opened a neighbouring menu.

   This version never relies on the browser's hover of overlapping
   elements. It decides which menu is open purely from where the
   pointer actually is:
   - over a trigger in the header row  -> that menu opens (after a
     70ms intent delay, so sweeping across the bar doesn't flash
     every panel);
   - over the open menu's own panel, or the gap between its trigger
     and panel -> it stays open;
   - anywhere else -> it closes after a short grace period.
   Closed panels are display:none, so nothing invisible can catch
   the mouse, and only one menu is ever open.
   Keyboard: Enter/Space on a trigger opens and pins it; Esc, tabbing
   away or clicking outside closes it.
   Touch / non-hover devices are left entirely to app.js. The open
   state is still the existing .touch-open class, so all existing
   dropdown styling is reused unchanged.
   ============================================================ */
(function () {
  var nav = document.querySelector('.header__subnav');
  if (!nav) return;

  var headerEl = document.getElementById('site-header') || nav;
  var mouseLike = window.matchMedia('(hover: hover) and (pointer: fine)');
  var OPEN_DELAY = 70;   // ms pointer must rest on a trigger before its menu opens
  var CLOSE_DELAY = 160; // ms grace before a menu closes once the pointer leaves it
  var PAD = 6;           // px of horizontal slack around each trigger

  var items = Array.prototype.slice.call(nav.querySelectorAll('.subnav-item--has-drop'));
  var current = null;    // the one open item
  var pinned = null;     // keyboard-opened item (survives mouse movement)
  var pending = null;    // item waiting out OPEN_DELAY
  var openTimer = 0, closeTimer = 0, closing = false, raf = 0, lastX = 0, lastY = 0;

  // Closed panels are removed from layout on mouse-like devices, so a
  // wide invisible panel can never sit under the pointer. If anything
  // below throws, this rule is removed again so the menus still work.
  var guard = document.createElement('style');
  guard.setAttribute('data-nav-dropdowns', '');
  guard.textContent =
    '@media (hover: hover) and (pointer: fine) {' +
    '.subnav-item--has-drop:not(.touch-open) > .subnav-linkdrop,' +
    '.subnav-item--has-drop:not(.touch-open) > .subnav-megadrop' +
    '{ display: none !important; }' +
    '}';
  document.head.appendChild(guard);

  try {
    var triggerOf = function (item) { return item.querySelector('.subnav-link--collections'); };
    var panelOf = function (item) { return item.querySelector('.subnav-linkdrop, .subnav-megadrop'); };
    var isDropItem = function (el) { return !!(el && el.classList && el.classList.contains('subnav-item--has-drop')); };

    var openItem = function (item) {
      current = item;
      item.classList.add('touch-open');
      var t = triggerOf(item);
      if (t) t.setAttribute('aria-expanded', 'true');
    };

    var closeItem = function (item) {
      item.classList.remove('touch-open');
      var t = triggerOf(item);
      if (t) t.setAttribute('aria-expanded', 'false');
      if (current === item) current = null;
      if (pinned === item) pinned = null;
    };

    var activate = function (item) {
      items.forEach(function (i) { if (i !== item) closeItem(i); });
      openItem(item);
    };

    var cancelClose = function () { clearTimeout(closeTimer); closing = false; };
    var scheduleClose = function () {
      if (closing) return;
      closing = true;
      closeTimer = setTimeout(function () {
        closing = false;
        if (current && !pinned) closeItem(current);
      }, CLOSE_DELAY);
    };

    // The header row (full bar height) x the trigger's own width.
    var triggerUnder = function (x, y) {
      var hr = headerEl.getBoundingClientRect();
      if (y < hr.top || y > hr.bottom) return null;
      var best = null, bestD = Infinity;
      items.forEach(function (item) {
        var t = triggerOf(item);
        if (!t) return;
        var r = t.getBoundingClientRect();
        if (!r.width || x < r.left - PAD || x > r.right + PAD) return;
        var d = Math.abs(x - (r.left + r.width / 2));
        if (d < bestD) { best = item; bestD = d; }
      });
      return best;
    };

    // Over the open panel, or crossing the gap between its trigger and it.
    var inSafeZone = function (item, x, y) {
      var p = panelOf(item);
      if (!p) return false;
      var pr = p.getBoundingClientRect();
      if (!pr.width || !pr.height) return false;
      if (x >= pr.left && x <= pr.right && y >= pr.top && y <= pr.bottom) return true;
      var t = triggerOf(item);
      if (!t) return false;
      var tr = t.getBoundingClientRect();
      var hr = headerEl.getBoundingClientRect();
      var top = Math.min(tr.bottom, hr.bottom);
      var left = Math.min(tr.left, pr.left), right = Math.max(tr.right, pr.right);
      return y >= top && y <= pr.top && x >= left && x <= right;
    };

    // Pointer is in the header row between triggers (the small gaps).
    var inNavGap = function (x, y) {
      var hr = headerEl.getBoundingClientRect(), nr = nav.getBoundingClientRect();
      return y >= hr.top && y <= hr.bottom && x >= nr.left && x <= nr.right;
    };

    var evaluate = function () {
      raf = 0;
      if (!mouseLike.matches) return;
      var x = lastX, y = lastY;
      var t = triggerUnder(x, y);
      if (t) {
        cancelClose();
        if (t === current) { clearTimeout(openTimer); pending = null; return; }
        if (t !== pending) {
          clearTimeout(openTimer);
          pending = t;
          openTimer = setTimeout(function () { pending = null; activate(t); }, OPEN_DELAY);
        }
        return;
      }
      clearTimeout(openTimer);
      pending = null;
      if (!current) return;
      if (inSafeZone(current, x, y) || inNavGap(x, y)) { cancelClose(); return; }
      if (!pinned) scheduleClose();
    };

    document.addEventListener('mousemove', function (e) {
      lastX = e.clientX;
      lastY = e.clientY;
      if (!raf) raf = requestAnimationFrame(evaluate);
    }, { passive: true });

    document.documentElement.addEventListener('mouseleave', function () {
      clearTimeout(openTimer);
      pending = null;
      if (current && !pinned) scheduleClose();
    });

    // Neutralise app.js's own hover handlers on the items (capture phase runs
    // first on the nav; stopPropagation keeps them from firing at all).
    ['mouseenter', 'mouseleave'].forEach(function (type) {
      nav.addEventListener(type, function (e) {
        if (mouseLike.matches && isDropItem(e.target)) e.stopPropagation();
      }, true);
    });

    // Clicking a menu button never toggles it shut. A keyboard-triggered
    // click (detail === 0) also pins it open.
    nav.addEventListener('click', function (e) {
      if (!mouseLike.matches) return;
      var btn = e.target.closest && e.target.closest('button.subnav-link--collections');
      if (!btn || !nav.contains(btn)) return;
      var item = btn.closest('.subnav-item--has-drop');
      if (!item) return;
      e.stopPropagation();
      cancelClose();
      activate(item);
      if (e.detail === 0) pinned = item;
    }, true);

    document.addEventListener('click', function (e) {
      if (pinned && !pinned.contains(e.target)) closeItem(pinned);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' || !current) return;
      var item = current;
      var hadFocus = item.contains(document.activeElement);
      items.forEach(closeItem);
      var t = triggerOf(item);
      if (hadFocus && t) t.focus();
    });

    nav.addEventListener('focusout', function (e) {
      var item = e.target.closest && e.target.closest('.subnav-item--has-drop');
      if (!item || !e.relatedTarget) return;
      if (!item.contains(e.relatedTarget)) closeItem(item);
    });
  } catch (err) {
    if (guard.parentNode) guard.parentNode.removeChild(guard);
    if (window.console) console.error('nav-dropdowns disabled:', err);
  }
})();
